import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

/**
 * Listado de facturas. Query propia (no generada) para poder incluir
 * `totalPrices` y mostrar el Monto en el listado — el codegen apunta al esquema
 * remoto, que aquí no está disponible.
 */
const MY_BILLINGS = gql`
  query PortalMyBillings(
    $businessId: String
    $take: Int
    $skip: Int
    $statusFilter: [String!]
    $search: String
    $fromDate: String
    $toDate: String
  ) {
    portalMyBillings(
      businessId: $businessId
      take: $take
      skip: $skip
      statusFilter: $statusFilter
      search: $search
      fromDate: $fromDate
      toDate: $toDate
    ) {
      items {
        id
        invoiceNumber
        status
        currencyId
        currency {
          id
          name
        }
        registeredAt
        businessId
        totalPrices {
          id
          amount
          currencyId
        }
      }
      totalItems
    }
  }
`

export interface BillingListItem {
  id: number
  invoiceNumber: string
  status: string
  currencyId?: number | null
  currency?: { id: number; name: string } | null
  registeredAt?: string | null
  businessId?: string | null
  totalPrices?: { id: number; amount: number; currencyId?: number | null }[] | null
}

interface MyBillingsResult {
  portalMyBillings: { items: BillingListItem[]; totalItems: number }
}

interface UseMyBillingsArgs {
  businessId?: string | null
  take: number
  skip: number
  statusFilter?: string[] | null
  search?: string | null
  fromDate?: string | null
  toDate?: string | null
  /** `skip` de paginación es el offset; éste desactiva la query (sin negocios → 403). */
  disabled?: boolean
}

/** Listado paginado de facturas del usuario, con filtros de estado, número y fecha. */
export function useMyBillings({
  businessId,
  take,
  skip,
  statusFilter,
  search,
  fromDate,
  toDate,
  disabled = false,
}: UseMyBillingsArgs) {
  const { data, loading, error, refetch } = useQuery<MyBillingsResult>(MY_BILLINGS, {
    skip: disabled,
    variables: {
      businessId: businessId ?? null,
      take,
      skip,
      statusFilter: statusFilter ?? null,
      search: search ?? null,
      fromDate: fromDate ?? null,
      toDate: toDate ?? null,
    },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  return {
    billings: data?.portalMyBillings.items ?? [],
    totalItems: data?.portalMyBillings.totalItems ?? 0,
    loading,
    error,
    refetch,
  }
}
